import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import DateTimeComponent, { DateTimeComponentProps } from './DateTimeComponent';
import GeocoordinateComponent, { GeocoordinateComponentProps } from './GeocoordinateComponent';
import BandComponent, { BandComponentProps } from './BandComponent';
import TagComponent, { TagComponentProps } from './TagComponent';
import MyCampaignComponent, { MyCampaignComponentProps } from './MyCampaignComponent';
import { useAppContext } from '../utils/AppContext';

export interface DatasetProps {
  id: string;
  name: string;
  created_at: string;
  count: number;
  latest_capture: string;
  earliest_capture: string;
};

export interface FetchFileListProps {
  count: number;
  next: string;
  previous: string;
  results: DatasetProps[];
};

interface FilterPanelProps extends
  DateTimeComponentProps,
  GeocoordinateComponentProps,
  BandComponentProps,
  TagComponentProps,
  MyCampaignComponentProps {
    updateCampaignFilter: boolean;
    onFilterChange?: () => void;
    onFiltersApplied?: () => void;
    onFiltersCleared?: () => void;
  };

export default function FilterPanel(props: FilterPanelProps) {
  const {
    updateCampaignFilter,
    startDate,
    setStartDate,
    setConfirmedStartDate,
    endDate,
    setEndDate,
    setConfirmedEndDate,
    timezoneOffset,
    setTimezoneOffset,
    defaultTimezoneOffset,
    northWest,
    southEast,
    setNorthWest,
    setSouthEast,
    tagName,
    setTagName,
    campaignName,
    setCampaignName,
    geoPolygon,
    setGeoPolygon,
    geoPolygonImage,
    setGeoPolygonImage,
    bandName,
    setBandName,
    onFilterChange,
    onFiltersApplied,
    onFiltersCleared,
  } = props;

  // check login status
  const context = useAppContext();

  // notification snackbar
  const { enqueueSnackbar } = useSnackbar()

  // Add queryClient
  const queryClient = useQueryClient();

  // reset flag to ensure all states are updated at first
  const [resetFlag, setResetFlag] = useState(false);

  const hasTagFilters = tagName.length > 0;
  const hasCampaignFilters = campaignName.length > 0;

  const [tagsExpanded, setTagsExpanded] = useState(hasTagFilters);
  const [campaignsExpanded, setCampaignsExpanded] = useState(hasCampaignFilters);

  useEffect(() => {
    if (hasTagFilters) {
      setTagsExpanded(true);
    }
  }, [hasTagFilters]);

  useEffect(() => {
    if (hasCampaignFilters) {
      setCampaignsExpanded(true);
    }
  }, [hasCampaignFilters]);

  // reset filters
  const handleReset = () => {
    setStartDate(null);
    setConfirmedStartDate(null);
    setEndDate(null);
    setConfirmedEndDate(null);
    setTimezoneOffset(defaultTimezoneOffset);
    setNorthWest('');
    setSouthEast('');
    setGeoPolygon('');
    setGeoPolygonImage('');
    setTagName([]);
    setCampaignName([]);
    setResetFlag(prevState => !prevState);
    setBandName([]);
    setTagsExpanded(false);
    setCampaignsExpanded(false);
    onFiltersCleared?.();
    // reset selected IDs
    if (onFilterChange) {
      onFilterChange();
    }
  };

  // State to hold the coordinates
  const handleSubmit = () => {
    // also strip [ { ( ) } ] characters from coordinates
    const northWestcoords = northWest?.replace(/[(){}\[\]]/g, '').split(',').map(coord => parseFloat(coord.trim()));
    const southEastcoords = southEast?.replace(/[(){}\[\]]/g, '').split(',').map(coord => parseFloat(coord.trim()));

    // check coordinates are valid only if input exists
    if (northWest !== '' || southEast !== '') {
      if (!(northWestcoords && northWestcoords.length === 2 &&
          !isNaN(northWestcoords[0]) && !isNaN(northWestcoords[1]) &&
          northWestcoords[0] < 90 && northWestcoords[0] > -90 &&
          northWestcoords[1] < 180 && northWestcoords[1] > -180 &&
          southEastcoords && southEastcoords.length === 2 &&
          !isNaN(southEastcoords[0]) && !isNaN(southEastcoords[1]) &&
          southEastcoords[0] < 90 && southEastcoords[0] > -90 &&
          southEastcoords[1] < 180 && southEastcoords[1] > -180 &&
          northWestcoords[0] > southEastcoords[0] &&
          northWestcoords[1] < southEastcoords[1]
      )) {
        enqueueSnackbar('Invalid geocoordinates: please check the inputs', {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
        return;
      }
    }

    // apply filter (refetch)
    queryClient.invalidateQueries({ queryKey: ['sigcap_datatable'] });

    onFiltersApplied?.();

    // reset selected IDs
    if (onFilterChange) {
      onFilterChange();
    }
  };

  // refetch data for reset filters
  useEffect(() => {
    queryClient.removeQueries({ queryKey: ['sigcap_datatable'] });
  }, [resetFlag, queryClient]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'top',
      justifyContent: 'left',
    }}>
      {/* Date/Time component */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography component="span"><b>Date/Time</b></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DateTimeComponent
            startDate={startDate}
            setStartDate={setStartDate}
            setConfirmedStartDate={setConfirmedStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setConfirmedEndDate={setConfirmedEndDate}
            timezoneOffset={timezoneOffset}
            setTimezoneOffset={setTimezoneOffset}
            defaultTimezoneOffset={defaultTimezoneOffset}
          />
        </AccordionDetails>
      </Accordion>
      {/* Geocoordinate component */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2-content"
          id="panel2-header"
        >
          <Typography component="span"><b>Geocoordinates</b></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GeocoordinateComponent
            northWest={northWest}
            southEast={southEast}
            setNorthWest={setNorthWest}
            setSouthEast={setSouthEast}
            geoPolygon={geoPolygon}
            setGeoPolygon={setGeoPolygon}
            geoPolygonImage={geoPolygonImage}
            setGeoPolygonImage={setGeoPolygonImage}
          />
        </AccordionDetails>
      </Accordion>
      {/* Band component */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3-content"
          id="panel3-header"
        >
          <Typography component="span"><b>Bands</b></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BandComponent
            bandName={bandName}
            setBandName={setBandName}
            disabled={context?.username == null}
          />
        </AccordionDetails>
      </Accordion>
      {context?.username && (
        <>
          {/* Tag component */}
          <Accordion
            disabled={context?.username == null}
            expanded={tagsExpanded}
            onChange={(_, expanded) => setTagsExpanded(expanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel4-content"
              id="panel4-header"
            >
              <Typography component="span"><b>Tags</b></Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TagComponent
                tagName={tagName}
                setTagName={setTagName}
                disabled={context?.username == null}
              />
            </AccordionDetails>
          </Accordion>
          {/* My Campaign component */}
          <Accordion
            disabled={context?.username == null}
            expanded={campaignsExpanded}
            onChange={(_, expanded) => setCampaignsExpanded(expanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel5-content"
              id="panel5-header"
            >
              <Typography component="span"><b>Campaigns</b></Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MyCampaignComponent
                campaignName={campaignName}
                setCampaignName={setCampaignName}
                disabled={context?.username == null}
                updateCampaignFilter={updateCampaignFilter}
              />
            </AccordionDetails>
          </Accordion>
        </>
      )}
      <div
        style={{
          display: 'flex',
          marginTop: '2em',
          marginBottom: '2em',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          type="submit"
          onClick={() => {handleReset()}}
        >
          Clear filters
        </Button>
        <Button
          variant="contained"
          color="primary"
          style={{ marginLeft: '2em' }}
          onClick={() => {handleSubmit()}}
          disabled={
            startDate === null &&
            endDate === null &&
            (!(northWest !== '' && southEast !== '')) &&
            geoPolygon === '' &&
            tagName.length === 0 &&
            campaignName.length === 0 &&
            bandName.length === 0
          }
        >
          Apply filters
        </Button>
      </div>
    </div>
  );
};
